import ViinexService from '@/services/viinex';
import {
    StreamingStatus,
    StreamingTimeline,
    TimelineInterval,
    ViinexStorageData,
} from '@/types/streaming';
import {
    STREAMING_TIMING,
    STREAMING_ERRORS,
    MILLISECONDS_TO_SECONDS,
} from '@/constants/time';

function sortIntervalsByBegin(
    intervals: TimelineInterval[],
): TimelineInterval[] {
    return [...intervals].sort((a, b) => a.begin - b.begin);
}

export function computeGaps(
    intervals: TimelineInterval[],
    currentTimestamp: number,
): TimelineInterval[] {
    if (intervals.length === 0) return [];

    const sortedIntervals = sortIntervalsByBegin(intervals);
    const { gaps, latest } = sortedIntervals.slice(1).reduce(
        ({ gaps, latest }, interval) => ({
            gaps:
                latest < interval.begin
                    ? [...gaps, { begin: latest, end: interval.begin }]
                    : gaps,
            latest: Math.max(latest, interval.end),
        }),
        { gaps: [] as TimelineInterval[], latest: sortedIntervals[0].end },
    );
    const recentFootageStart =
        currentTimestamp -
        STREAMING_TIMING.RECENT_FOOTAGE_TOLERANCE_MS / MILLISECONDS_TO_SECONDS;
    return latest < recentFootageStart
        ? [...gaps, { begin: latest, end: recentFootageStart }]
        : gaps;
}

export function isRecordingUnavailable(
    timestamp: number,
    timeline: Pick<StreamingTimeline, 'earliest' | 'intervals'> | null,
    currentTimestamp: number,
): boolean {
    if (!timeline) return false;

    return (
        (timeline.earliest !== null &&
            timestamp <
                timeline.earliest -
                    STREAMING_TIMING.ARCHIVE_START_TOLERANCE_MS /
                        MILLISECONDS_TO_SECONDS) ||
        computeGaps(timeline.intervals, currentTimestamp).some(
            ({ begin, end }) => timestamp > begin && timestamp < end,
        )
    );
}

export function timestampToProgress(
    timestamp: number,
    earliest: number,
    latest: number,
): number {
    if (latest === earliest) return 0;
    return (timestamp - earliest) / (latest - earliest);
}

export function progressToTimestamp(
    progress: number,
    earliest: number,
    latest: number,
): number {
    return earliest + progress * (latest - earliest);
}

export function calculateTimeWindow(
    targetTimestamp: number,
    windowMilliseconds: number,
): { beginMilliseconds: number; endMilliseconds: number } | null {
    const beginMilliseconds = Math.floor(
        targetTimestamp * MILLISECONDS_TO_SECONDS,
    );
    const endMilliseconds = Math.floor(beginMilliseconds + windowMilliseconds);

    if (
        !Number.isFinite(beginMilliseconds) ||
        !Number.isFinite(endMilliseconds) ||
        beginMilliseconds < 0 ||
        beginMilliseconds >= endMilliseconds
    ) {
        return null;
    }

    return {
        beginMilliseconds,
        endMilliseconds,
    };
}

export interface VodUrlSetters {
    setHlsUrl: (url: string | null) => void;
    setStreamBeginTimestamp: (begin: number | null) => void;
    setStatus: (status: StreamingStatus) => void;
    setError: (error: string | null) => void;
}

export async function buildVodUrl(
    sourceId: string,
    targetTimestamp: number | undefined,
    setters: VodUrlSetters,
): Promise<void> {
    const { setHlsUrl, setStreamBeginTimestamp, setStatus, setError } = setters;

    setStatus(StreamingStatus.LOADING);
    setError(null);
    setHlsUrl(null);
    setStreamBeginTimestamp(null);

    try {
        let archive: ViinexStorageData | null = null;
        try {
            archive = await ViinexService.fetchStorageData(sourceId);
        } catch (error) {
            // Viinex can serve timestamped HLS before archive metadata exists.
            if (targetTimestamp === undefined) throw error;
        }

        const timeWindow =
            targetTimestamp !== undefined
                ? calculateTimeWindow(
                      targetTimestamp,
                      STREAMING_TIMING.DEFAULT_VOD_WINDOW_MS,
                  )
                : archive
                  ? calculateTimeWindow(
                        Math.max(
                            archive.boundaries.earliestMilliseconds,
                            archive.boundaries.latestMilliseconds -
                                STREAMING_TIMING.DEFAULT_VOD_WINDOW_MS,
                        ) / MILLISECONDS_TO_SECONDS,
                        Math.min(
                            STREAMING_TIMING.DEFAULT_VOD_WINDOW_MS,
                            archive.boundaries.latestMilliseconds -
                                archive.boundaries.earliestMilliseconds,
                        ),
                    )
                  : null;

        if (!timeWindow) {
            resetVodState(STREAMING_ERRORS.INVALID_TIMESTAMPS, setters);
            return;
        }

        if (
            archive &&
            isRecordingUnavailable(
                timeWindow.beginMilliseconds / MILLISECONDS_TO_SECONDS,
                {
                    earliest: archive.boundaries.earliestSeconds,
                    intervals: archive.intervals,
                },
                Date.now() / MILLISECONDS_TO_SECONDS,
            )
        ) {
            resetVodState(STREAMING_ERRORS.NO_RECORDING, setters);
            return;
        }

        const storageService =
            archive?.storageService ??
            (await ViinexService.getVideoStorageServiceName(sourceId));
        const url = ViinexService.buildHlsUrl(
            storageService,
            sourceId,
            timeWindow.beginMilliseconds,
            timeWindow.endMilliseconds,
        );

        setHlsUrl(url);
        setStreamBeginTimestamp(
            timeWindow.beginMilliseconds / MILLISECONDS_TO_SECONDS,
        );
        setStatus(StreamingStatus.READY);
    } catch (err) {
        const message =
            err instanceof Error ? err.message : STREAMING_ERRORS.LOAD_FAILED;
        resetVodState(message, setters);
    }
}

export function resetVodState(message: string, setters: VodUrlSetters): void {
    const { setHlsUrl, setStreamBeginTimestamp, setStatus, setError } = setters;

    setError(message);
    setStatus(StreamingStatus.ERROR);
    setHlsUrl(null);
    setStreamBeginTimestamp(null);
}
